import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Mail, Pencil, Plus, Search, Trash2, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { LetterEditor } from '@/components/admin/letters/LetterEditor';
import { CustomerPickerDialog } from '@/components/admin/letters/CustomerPickerDialog';
import { LetterEmailDialog } from '@/components/admin/letters/LetterEmailDialog';
import { downloadLetterPdf, todayDE } from '@/components/admin/letters/letterActions';

const toForm = (l) => ({
  ...l,
  recipient_name: l.billing_name || '',
  recipient_street: l.billing_street || '',
  recipient_city: l.billing_city || '',
});

const LetterRow = ({ letter, onOpen, onEmail, onDelete }) => (
  <div className="group flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b last:border-0 hover:bg-[#9B1B30]/[0.03] transition-colors" data-testid={`letter-row-${letter.id}`}>
    <button type="button" onClick={() => onOpen(letter)} className="flex-1 min-w-0 text-left">
      <span className="block font-medium text-sm truncate">{letter.subject}</span>
      <span className="block text-xs text-gray-500 truncate">
        {letter.document_number} · {letter.billing_name} · {letter.document_date}
      </span>
    </button>
    <div className="flex gap-1 shrink-0">
      <Button variant="ghost" size="sm" onClick={() => onOpen(letter)} title="Bearbeiten" data-testid={`letter-edit-${letter.id}`}><Pencil className="w-4 h-4" /></Button>
      <Button variant="ghost" size="sm" onClick={() => downloadLetterPdf(letter)} title="PDF herunterladen" data-testid={`letter-download-${letter.id}`}><Download className="w-4 h-4" /></Button>
      <Button variant="ghost" size="sm" onClick={() => onEmail(letter)} title="Per E-Mail senden" data-testid={`letter-email-${letter.id}`}><Mail className="w-4 h-4" /></Button>
      <Button variant="ghost" size="sm" onClick={() => onDelete(letter)} title="Löschen" className="text-red-500 hover:text-red-700" data-testid={`letter-delete-${letter.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

export default function AdminLetters() {
  const [letters, setLetters] = useState([]);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [picker, setPicker] = useState(false);
  const [emailLetter, setEmailLetter] = useState(null);

  const load = useCallback(() => api.get('/admin/letters').then(({ data }) => setLetters(data)).catch(() => toast.error('Briefe konnten nicht geladen werden')), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return letters.filter((l) => !q || [l.subject, l.billing_name, l.document_number].some((v) => (v || '').toLowerCase().includes(q)));
  }, [letters, query]);

  const startNew = (recipient) => {
    setPicker(false);
    setEditing({ ...recipient, subject: '', body_html: '', document_date: todayDE() });
  };

  const remove = async (letter) => {
    if (!window.confirm(`Brief ${letter.document_number} wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/documents/${letter.id}`);
      toast.success('Brief gelöscht');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="admin-letters-page">
      {editing ? (
        <LetterEditor key={editing.id || 'new'} initial={editing} onBack={() => { setEditing(null); load(); }} onSaved={load} onEmail={setEmailLetter} />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Briefe</h1>
              <p className="text-sm text-gray-500 mt-1">Schreiben im Firmen-Briefkopf – speichern, als PDF laden oder per E-Mail senden.</p>
            </div>
            <Button onClick={() => setPicker(true)} className="bg-[#9B1B30] hover:bg-[#7a1526] text-white" data-testid="new-letter-btn">
              <Plus className="w-4 h-4 mr-2" /> Neuer Brief
            </Button>
          </div>
          <div className="relative mb-4 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Betreff, Empfänger oder Nummer" className="pl-9" data-testid="letter-search-input" />
          </div>
          <div className="rounded-xl border bg-white overflow-hidden" data-testid="letter-list">
            {filtered.map((l) => (
              <LetterRow key={l.id} letter={l} onOpen={(x) => setEditing(toForm(x))} onEmail={setEmailLetter} onDelete={remove} />
            ))}
            {filtered.length === 0 && (
              <div className="py-16 text-center text-gray-500" data-testid="letter-list-empty">
                <ScrollText className="w-10 h-10 mx-auto mb-3 text-[#9B1B30]/40" />
                <p className="text-sm">{letters.length ? 'Keine Treffer' : 'Noch keine Briefe – lege mit „Neuer Brief" los.'}</p>
              </div>
            )}
          </div>
        </>
      )}
      <CustomerPickerDialog open={picker} onOpenChange={setPicker} onPick={startNew} />
      <LetterEmailDialog letter={emailLetter} onClose={() => setEmailLetter(null)} />
    </div>
  );
}
